import { Add, CancelRounded } from "@mui/icons-material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { Dialog, DialogContent } from '@mui/material';
import Tooltip from "@mui/material/Tooltip";
import ExcelExport from 'customComponents/AdminComponents/ExcelExport';
import TotalCostCard from "customComponents/TotalCostCard";
import { calculateTotalCost } from 'lib/utilities/calculateCost';
import { dataParser } from "lib/utilities/parseDataFromSheet";
import { UserAuth } from 'provider/AuthProvider';
import { useRef, useState } from "react";
import { toast } from 'react-toastify';
import * as XLSX from "xlsx";

const AddExcelModalCard = ({
  text,
  handleSubmit,
  candidates,
  setCandidates,
  isLoading,
  setIsOpening,
  experience,
  totalCustomSkills,
}) => {
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const fileInputRef = useRef(null);

  const { wallet, experiencePrice, coinPrice, skillPrice } = UserAuth();

  const handleOpen = (e) => {
    e.stopPropagation();
    setOpen(true);
    setIsOpening(true);
  };

  const handleClose = () => {
    setOpen(false);
    clearFileInput();
    setIsOpening(false);
  };

  const handleCandidates = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = (event) => {
        const data = event.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsedData =dataParser(sheet);
        setCandidates(parsedData);
        const requiredCoins = calculateTotalCost(parsedData.length, experience, experiencePrice, coinPrice, skillPrice, totalCustomSkills).totalCoins;
        if (wallet?.Coins < requiredCoins) {
          setDisabled(true);
          toast.warning(
            `Insufficient coins! You need ${requiredCoins} coins but have ${wallet?.Coins} coins available.`
          );
        } else {
          setDisabled(false);
        }
      };
    } else {
      console.error("No file selected or file type is incorrect.");
    }
  };

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      setCandidates([]);
    }
  };

  const handleExcelSubmit = async (e) => {
    const requiredCoins = calculateTotalCost(candidates.length, experience, experiencePrice, coinPrice, skillPrice, totalCustomSkills).totalCoins;
    if (wallet?.Coins >= requiredCoins) {
      await handleSubmit(e);
      handleClose();
    } else {
      setDisabled(true);
      toast.warning(
        `Insufficient coins! You need ${requiredCoins} coins but have ${wallet?.Coins} coins available.`
      );
    }
  };

  return (
    <div>
      {text ?
        <button onClick={handleOpen} className="bg-blue-50 border text-blue-700 border-blue-200 hover:bg-blue-100 px-4 py-1.5 flex items-center rounded-lg transition-colors duration-150 text-sm font-medium">
          <span className='mr-2'>Add Candidates</span>
          <Add className='w-6 h-6' />
        </button>
        :
        <Tooltip title="Add Excel" placement="right">
          <PostAddIcon
            style={{
              color: "#006566",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={handleOpen}
            fontSize="medium"
          />
        </Tooltip>
      }
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleExcelSubmit}>
          <div className='flex justify-between items-center border-b px-5 py-2'>
            <span className='font-semibold text-indigo-900'>
              Add Candidates
            </span>
            <button
              className="text-emerald-700 text-sm font-medium bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-4 py-1.5 rounded-lg flex items-center disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 disabled:border-gray-200 transition-colors duration-150"
              type="submit"
              disabled={isLoading || disabled}
            >
              <span className="mr-2 leading-none">
                {isLoading ? "Uploading..." : "Upload"}
              </span>
              <Add style={{ height: "20px", width: "20px" }} />
            </button>
          </div>
          <DialogContent>
            <div className="flex flex-col gap-4">
              <div className="flex flex-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx, .xls"
                  required
                  onChange={handleCandidates}
                  className="file:py-0.5 file:px-2 file:bg-blue-50 file:border file:border-blue-200 hover:file:bg-blue-100 file:rounded-lg file:text-sm file:text-blue-700 file:font-medium file:outline-none file:ring-0 file:focus:ring-0 file:focus:outline-none file:shadow-none file:cursor-pointer file:mr-4 p-1.5 flex flex-1 text-sm text-gray-700 cursor-pointer border rounded-lg border-r-0 border-gray-300 rounded-r-none"
                />
                <button
                  type="button"
                  className="w-40 flex items-center justify-center px-4 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors duration-200 border border-r-0 border-red-200"
                  onClick={clearFileInput}
                >
                  <span className="mr-2">Reset File Input</span>
                  <CancelRounded style={{ width: "18px", height: "18px" }} />
                </button>
                <ExcelExport />
              </div>

              <span className="text-sm font-normal text-red-500">
                *Important: Please ensure that you insert the file with the exact headings, word for word, as provided.
                Failing to do so may result in errors or incomplete data processing. This is crucial to ensure the file is correctly parsed and processed.*
              </span>

              {candidates.length > 0 && (
                <TotalCostCard formData={{ candidates, experience }} customSkills={totalCustomSkills} />
              )}
            </div>
          </DialogContent>
        </form>
      </Dialog>
    </div>
  );
};

export default AddExcelModalCard;